import React from 'react';
import type { RfqHubFileRelease, RfqPackage, VendorFileRelease } from '../../types';
import type { VendorPackageVersionRow } from '../../utils/vendorPackageVersion';
import {
  shouldDeemphasizeRfqVersion,
  vendorPackageVersionLabelForPackage,
  vendorPackageVersionName,
} from '../../utils/vendorPackageVersion';
import { formatSupplierDesignVersionSubtitle, proposalRfqLinkFromPackage } from '../../utils/proposalRfqVersionLink';
import { isReferenceRelease } from '../../utils/rfqReleasePurpose';
import RfqVersionTimelineNode from './RfqVersionTimelineNode';
import './TimberRfqReleasePanel.css';
import './VendorPortalVersionTree.css';

export interface VendorPortalVersionTreeProps {
  versions: VendorPackageVersionRow[];
  selectedPackageId: string | null;
  onSelect?: (packageId: string) => void;
  hubReleaseById?: Map<string, RfqHubFileRelease>;
  vendorReleases?: VendorFileRelease[];
  orgId?: string;
  /** Per-package approved badge labels (e.g. "Approved by GR"). */
  approvedPackageBadgeLabels?: Map<string, string>;
}

const VendorPortalVersionTree: React.FC<VendorPortalVersionTreeProps> = ({
  versions,
  selectedPackageId,
  onSelect,
  hubReleaseById,
  vendorReleases,
  orgId,
  approvedPackageBadgeLabels,
}) => {
  if (!versions.length) return null;

  const interactive = versions.length > 1 && !!onSelect;
  const lastIndex = versions.length - 1;

  return (
    <nav className="vp-version-tree" aria-label="Package versions">
      <ol className="vp-version-list">
        {versions.map((row, idx) => {
          const id = row.pkg.id || '';
          const active = selectedPackageId === id;
          const isFirstNode = idx === 0;
          const isLastNode = idx === lastIndex;
          const versionInactive = shouldDeemphasizeRfqVersion(row, versions);
          const versionApprovedLabel = approvedPackageBadgeLabels?.get(id);
          const versionApproved = !!versionApprovedLabel;

          const versionName = vendorPackageVersionName(row.pkg, {
            hubReleaseById,
            vendorReleases,
            orgId,
          });

          const designVersionSubtitle = formatSupplierDesignVersionSubtitle(
            proposalRfqLinkFromPackage(row.pkg),
          );

          const label = (
            <>
              <span className="vp-version-title">
                <span className="vp-version-rev">{vendorPackageVersionLabelForPackage(row.pkg)}</span>
                {versionName && (
                  <span className="vp-version-label">{versionName}</span>
                )}
                {isReferenceRelease(row.pkg) && (
                  <span
                    className="vp-version-badge vp-version-badge--reference"
                    title="Refined file for your approved quote — build from it, do not requote."
                  >
                    Reference
                  </span>
                )}
                {versionApproved ? (
                  <span className="vp-version-badge vp-version-badge--approved">
                    {versionApprovedLabel}
                  </span>
                ) : row.isLatest ? (
                  <span className="vp-version-badge vp-version-badge--latest">Latest</span>
                ) : null}
              </span>
              {designVersionSubtitle && (
                <span className="vp-version-design">{designVersionSubtitle}</span>
              )}
              {row.pkg.createdAt instanceof Date && (
                <span className="vp-version-date">
                  Published {row.pkg.createdAt.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              )}
            </>
          );

          return (
            <li
              key={id}
              className={`vp-version-item${isFirstNode ? ' vp-version-item--first-node' : ''}${
                isLastNode ? ' vp-version-item--last-node' : ''
              }${isLastNode ? ' vp-version-item--last' : ''}${
                versionInactive ? ' vp-version-item--inactive' : ''
              }`}
            >
              <div className="vp-version-track" aria-hidden>
                <RfqVersionTimelineNode selected={active} />
              </div>
              <div className={`vp-version-row${active ? ' vp-version-row--active' : ''}`}>
                {interactive ? (
                  <button
                    type="button"
                    className="vp-version-select"
                    onClick={() => onSelect?.(id)}
                    aria-current={active ? 'true' : undefined}
                  >
                    {label}
                  </button>
                ) : (
                  <div className="vp-version-select" aria-current={active ? 'true' : undefined}>
                    {label}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default VendorPortalVersionTree;
